import { PROGRESSION_LIBRARY, SCALE_LIBRARY, type ChordQuality } from './curriculum'
import { createScaleContext, pitchNameInContext, scaleDisplayName, type ScaleContext } from './pitchContext'
import type { AbsolutePitchSource, AudioEvent, Difficulty, ExerciseKind, PlaybackStyle, Question, QuestionAnswerParts } from './types'

export const NOTE_NAMES = ['C', 'C♯', 'D', 'E♭', 'E', 'F', 'F♯', 'G', 'A♭', 'A', 'B♭', 'B'] as const

type IntervalDefinition = {
  name: string
  semitones: number
  minimum: Difficulty
}

type ChordDefinition = {
  name: string
  intervals: number[]
  minimum: Difficulty
  inversionAmbiguous?: boolean
}

const difficultyRank: Record<Difficulty, number> = {
  beginner: 0,
  intermediate: 1,
  advanced: 2,
}

export const INTERVALS: IntervalDefinition[] = [
  { name: 'Perfect unison', semitones: 0, minimum: 'beginner' },
  { name: 'Minor 2nd', semitones: 1, minimum: 'intermediate' },
  { name: 'Major 2nd', semitones: 2, minimum: 'beginner' },
  { name: 'Minor 3rd', semitones: 3, minimum: 'beginner' },
  { name: 'Major 3rd', semitones: 4, minimum: 'beginner' },
  { name: 'Perfect 4th', semitones: 5, minimum: 'beginner' },
  { name: 'Tritone', semitones: 6, minimum: 'intermediate' },
  { name: 'Perfect 5th', semitones: 7, minimum: 'beginner' },
  { name: 'Minor 6th', semitones: 8, minimum: 'intermediate' },
  { name: 'Major 6th', semitones: 9, minimum: 'intermediate' },
  { name: 'Minor 7th', semitones: 10, minimum: 'advanced' },
  { name: 'Major 7th', semitones: 11, minimum: 'advanced' },
  { name: 'Perfect octave', semitones: 12, minimum: 'beginner' },
]

export const CHORDS: ChordDefinition[] = [
  { name: 'Major triad', intervals: [0, 4, 7], minimum: 'beginner' },
  { name: 'Minor triad', intervals: [0, 3, 7], minimum: 'beginner' },
  { name: 'Diminished triad', intervals: [0, 3, 6], minimum: 'intermediate' },
  { name: 'Augmented triad', intervals: [0, 4, 8], minimum: 'intermediate', inversionAmbiguous: true },
  { name: 'Dominant 7th', intervals: [0, 4, 7, 10], minimum: 'intermediate' },
  { name: 'Major 7th', intervals: [0, 4, 7, 11], minimum: 'intermediate' },
  { name: 'Minor 7th', intervals: [0, 3, 7, 10], minimum: 'intermediate' },
  { name: 'Half-diminished 7th', intervals: [0, 3, 6, 10], minimum: 'advanced' },
  { name: 'Diminished 7th', intervals: [0, 3, 6, 9], minimum: 'advanced', inversionAmbiguous: true },
  { name: 'Minor-major 7th', intervals: [0, 3, 7, 11], minimum: 'advanced' },
]

export const PROGRESSIONS = PROGRESSION_LIBRARY
const inversionNames = ['Root position', 'First inversion', 'Second inversion', 'Third inversion']

function eligible<T extends { minimum: Difficulty }>(items: T[], difficulty: Difficulty): T[] {
  return items.filter((item) => difficultyRank[item.minimum] <= difficultyRank[difficulty])
}

function pick<T>(items: T[], random: () => number): T {
  return items[Math.floor(random() * items.length)]
}

function shuffle<T>(items: T[], random: () => number): T[] {
  const copy = [...items]
  for (let index = copy.length - 1; index > 0; index -= 1) {
    const target = Math.floor(random() * (index + 1))
    ;[copy[index], copy[target]] = [copy[target], copy[index]]
  }
  return copy
}

function choicesFor(answer: string, candidates: string[], random: () => number, count = 5): string[] {
  const distractors = shuffle(
    candidates.filter((candidate) => candidate !== answer),
    random,
  ).slice(0, Math.min(count - 1, candidates.length - 1))
  return shuffle([answer, ...distractors], random)
}

function midiName(midi: number): string {
  return NOTE_NAMES[((midi % 12) + 12) % 12]
}

function triadFor(root: number, quality: ChordQuality): number[] {
  switch (quality) {
    case 'minor':
      return [root, root + 3, root + 7]
    case 'minor7':
      return [root, root + 3, root + 7, root + 10]
    case 'diminished':
      return [root, root + 3, root + 6]
    case 'diminished7':
      return [root, root + 3, root + 6, root + 9]
    case 'halfDiminished':
      return [root, root + 3, root + 6, root + 10]
    case 'dominant7':
      return [root, root + 4, root + 7, root + 10]
    case 'major7':
      return [root, root + 4, root + 7, root + 11]
    case 'augmented':
      return [root, root + 4, root + 8]
    case 'sus4':
      return [root, root + 5, root + 7]
    default:
      return [root, root + 4, root + 7]
  }
}

function chordEvents(notes: number[], style: PlaybackStyle, speed: number): AudioEvent[] {
  const duration = Math.max(0.45, 1.05 / speed)
  if (style === 'harmonic') return [{ notes, offset: 0, duration }]
  return notes.map((note, index) => ({ notes: [note], offset: index * (0.2 / speed), duration: 0.52 / speed }))
}

function noteQuestion(octaveRange: number, random: () => number, context?: ScaleContext): Question {
  const degree = context ? Math.floor(random() * context.pitchClasses.length) : -1
  const pitchClass = context ? context.pitchClasses[degree] : Math.floor(random() * 12)
  const availableOctaves = Math.min(4, Math.max(1, octaveRange))
  const octaveOffset = Math.floor(random() * availableOctaves) * 12
  const midi = 60 + pitchClass + octaveOffset
  const answer = context ? context.noteNames[degree] : NOTE_NAMES[pitchClass]
  const canonicalAnswer = NOTE_NAMES[pitchClass]
  return {
    id: crypto.randomUUID(),
    kind: 'notes',
    skill: 'Absolute pitch · note class',
    prompt: 'Which note did you hear?',
    answer,
    acceptedAnswers: canonicalAnswer === answer ? undefined : [canonicalAnswer],
    choices: choicesFor(answer, context ? context.noteNames : [...NOTE_NAMES], random, 6),
    events: [{ notes: [midi], offset: 0, duration: 1.1 }],
    explanation: `${answer} was played in octave ${Math.floor(midi / 12) - 1}.`,
    detail: context
      ? `${context.tonicName} ${scaleDisplayName(context.scale)} context · no reference tone is played.`
      : 'Listen for the note’s color without relying on a reference tone.',
  }
}

function intervalQuestion(difficulty: Difficulty, style: PlaybackStyle, speed: number, random: () => number): Question {
  const available = eligible(INTERVALS, difficulty)
  const interval = pick(available, random)
  const root = 48 + Math.floor(random() * 12)
  const notes = [root, root + interval.semitones]
  const events = style === 'harmonic'
    ? [{ notes, offset: 0, duration: 1.1 / speed }]
    : notes.map((note, index) => ({ notes: [note], offset: index * (0.58 / speed), duration: 0.82 / speed }))
  return {
    id: crypto.randomUUID(),
    kind: 'intervals',
    skill: `Intervals · ${style}`,
    prompt: 'Which interval did you hear?',
    answer: interval.name,
    choices: choicesFor(interval.name, available.map((item) => item.name), random),
    events,
    explanation: `${midiName(root)} to ${midiName(root + interval.semitones)} spans ${interval.semitones} semitone${interval.semitones === 1 ? '' : 's'}: a ${interval.name.toLowerCase()}.`,
    detail: style === 'harmonic' ? 'The notes sound together.' : 'The notes sound one after another.',
  }
}

function scaleQuestion(selectedIds: string[] | undefined, speed: number, random: () => number): Question {
  const selected = SCALE_LIBRARY.filter((scale) => selectedIds?.includes(scale.id))
  const available = selected.length ? selected : SCALE_LIBRARY
  const scale = pick(available, random)
  const root = 48 + Math.floor(random() * 12)
  const spacing = Math.max(0.2, 0.34 / speed)
  const notes = [...scale.intervals, 12].map((interval) => root + interval)
  const familyNames = SCALE_LIBRARY.filter((item) => item.family === scale.family).map((item) => item.name)
  const candidates = [...new Set([...available.map((item) => item.name), ...familyNames, ...SCALE_LIBRARY.map((item) => item.name)])]
  return {
    id: crypto.randomUUID(),
    kind: 'scales',
    skill: `Scales and modes · ${scale.family}`,
    prompt: 'Which scale or mode did you hear?',
    answer: scale.name,
    choices: choicesFor(scale.name, candidates, random),
    events: notes.map((note, index) => ({ notes: [note], offset: index * spacing, duration: Math.max(0.3, spacing * 1.05) })),
    explanation: `${scale.name} on ${midiName(root)}. Formula: ${scale.formula}.`,
    detail: scale.description,
  }
}

function chordQuestion(
  difficulty: Difficulty,
  style: PlaybackStyle,
  speed: number,
  random: () => number,
  inversionOnly = false,
): Question {
  const available = eligible(CHORDS, difficulty)
  const chordPool = inversionOnly ? available.filter((item) => item.intervals.length === 3) : available
  const chord = pick(chordPool, random)
  const root = 48 + Math.floor(random() * 12)
  const maxInversion = inversionOnly ? Math.min(chord.intervals.length - 1, difficulty === 'beginner' ? 1 : 2) : 0
  const inversion = inversionOnly ? Math.floor(random() * (maxInversion + 1)) : 0
  const notes = chord.intervals.map((interval) => root + interval)
  for (let index = 0; index < inversion; index += 1) notes[index] += 12
  notes.sort((a, b) => a - b)
  const answer = inversionOnly ? inversionNames[inversion] : chord.name
  const candidates = inversionOnly
    ? inversionNames.slice(0, maxInversion + 1)
    : chordPool.map((item) => item.name)
  return {
    id: crypto.randomUUID(),
    kind: inversionOnly ? 'inversions' : 'chords',
    skill: inversionOnly ? 'Chord inversions' : `Chord quality · ${style}`,
    prompt: inversionOnly ? `Which inversion of a ${chord.name.toLowerCase()} did you hear?` : 'Which chord did you hear?',
    answer,
    choices: choicesFor(answer, candidates, random, inversionOnly ? candidates.length : 5),
    events: chordEvents(notes, style, speed),
    explanation: inversionOnly
      ? `${answer}: ${midiName(notes[0])} is the bass note of this ${chord.name.toLowerCase()}.`
      : `${chord.name} on ${midiName(root)} contains ${chord.intervals.map((value) => value).join('–')} semitones above its root.`,
    detail: inversionOnly ? 'Track the bass note first, then hear the quality above it.' : `Played ${style === 'harmonic' ? 'as a block' : 'as a broken chord'}.`,
  }
}

type AbsoluteChordMode = 'root' | 'root-quality' | 'root-quality-inversion'

function voicedChord(chord: ChordDefinition, root: number, inversion: number): number[] {
  const notes = chord.intervals.map((interval) => root + interval)
  for (let index = 0; index < inversion; index += 1) notes[index] += 12
  return notes.sort((a, b) => a - b)
}

function maximumAbsoluteInversion(chord: ChordDefinition, difficulty: Difficulty, mode: AbsoluteChordMode): number {
  if (chord.inversionAmbiguous) return 0
  if (mode === 'root-quality-inversion') {
    if (difficulty === 'beginner') return Math.min(1, chord.intervals.length - 1)
    return chord.intervals.length - 1
  }
  if (difficulty === 'beginner') return 0
  if (difficulty === 'intermediate') return Math.min(2, chord.intervals.length - 1)
  return chord.intervals.length - 1
}

function absoluteChordPool(difficulty: Difficulty, mode: AbsoluteChordMode): ChordDefinition[] {
  const available = eligible(CHORDS, difficulty)
  if (mode === 'root') {
    const unambiguous = available.filter((chord) => !chord.inversionAmbiguous)
    return difficulty === 'intermediate' ? unambiguous.filter((chord) => chord.intervals.length === 3) : unambiguous
  }
  if (mode !== 'root-quality-inversion') return available
  if (difficulty === 'intermediate') return available.filter((chord) => chord.intervals.length === 3 && !chord.inversionAmbiguous)
  return available.filter((chord) => !chord.inversionAmbiguous)
}

type DiatonicChordCandidate = {
  chord: ChordDefinition
  rootDegree: number
  noteDegrees: number[]
}

function intervalsMatch(first: number[], second: number[]): boolean {
  return first.length === second.length && first.every((value, index) => value === second[index])
}

function chordSizesFor(difficulty: Difficulty, mode: AbsoluteChordMode): number[] {
  if (difficulty === 'beginner') return [3]
  if (difficulty === 'intermediate' && mode !== 'root-quality') return [3]
  return [3, 4]
}

function diatonicChordCandidates(context: ScaleContext, difficulty: Difficulty, mode: AbsoluteChordMode): DiatonicChordCandidate[] {
  const definitions = eligible(CHORDS, difficulty)
  const scaleSize = context.scale.intervals.length
  const degreeSemitones = (absoluteDegree: number) => context.scale.intervals[absoluteDegree % scaleSize] + Math.floor(absoluteDegree / scaleSize) * 12
  const candidates: DiatonicChordCandidate[] = []

  for (let rootDegree = 0; rootDegree < scaleSize; rootDegree += 1) {
    for (const size of chordSizesFor(difficulty, mode)) {
      const noteDegrees = Array.from({ length: size }, (_, index) => rootDegree + index * 2)
      const rootSemitones = degreeSemitones(rootDegree)
      const intervals = noteDegrees.map((degree) => degreeSemitones(degree) - rootSemitones)
      const chord = definitions.find((definition) => intervalsMatch(definition.intervals, intervals))
      if (!chord) continue
      if ((mode === 'root' || mode === 'root-quality-inversion') && chord.inversionAmbiguous) continue
      candidates.push({ chord, rootDegree, noteDegrees })
    }
  }

  return candidates
}

function unique(items: string[]): string[] {
  return [...new Set(items)]
}

function scaleAwareChordQuestion(
  difficulty: Difficulty,
  octaveRange: number,
  style: PlaybackStyle,
  speed: number,
  random: () => number,
  mode: AbsoluteChordMode,
  context: ScaleContext,
): Question {
  const candidates = diatonicChordCandidates(context, difficulty, mode)
  const candidate = pick(candidates, random)
  const { chord, rootDegree, noteDegrees } = candidate
  const availableOctaves = Math.min(3, Math.max(1, octaveRange))
  const rootPitchClass = context.pitchClasses[rootDegree]
  const root = 48 + Math.floor(random() * availableOctaves) * 12 + rootPitchClass
  const maxInversion = maximumAbsoluteInversion(chord, difficulty, mode)
  const inversion = Math.floor(random() * (maxInversion + 1))
  const notes = voicedChord(chord, root, inversion)
  const rootName = context.noteNames[rootDegree]
  const canonicalRoot = midiName(rootPitchClass)
  const chordToneNames = noteDegrees.map((degree) => context.noteNames[degree % context.noteNames.length])
  const rootAliases = chord.inversionAmbiguous
    ? unique(chordToneNames.flatMap((name, index) => [name, midiName(context.pitchClasses[noteDegrees[index] % context.pitchClasses.length])]))
    : unique([rootName, canonicalRoot])
  const answerParts: QuestionAnswerParts = { root: rootName }

  if (mode !== 'root') answerParts.quality = chord.name
  if (mode === 'root-quality-inversion') answerParts.inversion = inversionNames[inversion]

  const answer = [rootName, mode !== 'root' ? chord.name : '', mode === 'root-quality-inversion' ? inversionNames[inversion] : '']
    .filter(Boolean)
    .join(' · ')
  const rootChoices = unique(candidates.map((item) => context.noteNames[item.rootDegree]))
  const qualityChoices = choicesFor(chord.name, unique(candidates.map((item) => item.chord.name)), random)
  const inversionChoices = inversionNames.slice(0, difficulty === 'advanced' ? inversionNames.length : maxInversion + 1)
  const kind: ExerciseKind = mode === 'root' ? 'chord-roots' : mode === 'root-quality' ? 'chords' : 'absolute-inversions'
  const soundedNames = notes.map((note) => pitchNameInContext(context, note) ?? midiName(note))

  return {
    id: crypto.randomUUID(),
    kind,
    skill: mode === 'root'
      ? 'Absolute pitch · scale-aware chord root'
      : mode === 'root-quality'
        ? 'Absolute pitch + harmony · scale-aware root and quality'
        : 'Absolute pitch + harmony · scale-aware chord and inversion',
    prompt: mode === 'root'
      ? 'What is the chord’s root note?'
      : mode === 'root-quality'
        ? 'Identify the root and chord quality.'
        : 'Identify the root, quality, and inversion.',
    answer,
    acceptedAnswers: mode === 'root' ? rootAliases.filter((name) => name !== rootName) : undefined,
    answerParts,
    acceptedAnswerParts: mode === 'root' ? undefined : { root: rootAliases },
    choices: mode === 'root' ? choicesFor(rootName, rootChoices, random, 6) : [],
    choiceGroups: mode === 'root'
      ? undefined
      : {
          root: context.noteNames,
          quality: qualityChoices,
          ...(mode === 'root-quality-inversion' ? { inversion: inversionChoices } : {}),
        },
    events: chordEvents(notes, style, speed),
    explanation: `${answer}. The sounded notes were ${soundedNames.join('–')} in ${context.tonicName} ${scaleDisplayName(context.scale)}.`,
    detail: mode === 'root'
      ? `All chord tones come from ${context.tonicName} ${scaleDisplayName(context.scale)}.`
      : mode === 'root-quality'
        ? `Diatonic root and quality are each worth half credit in ${context.tonicName} ${scaleDisplayName(context.scale)}.`
        : `This diatonic chord and every inversion remain inside ${context.tonicName} ${scaleDisplayName(context.scale)}.`,
  }
}

function absoluteChordQuestion(
  difficulty: Difficulty,
  octaveRange: number,
  style: PlaybackStyle,
  speed: number,
  random: () => number,
  mode: AbsoluteChordMode,
  context?: ScaleContext,
): Question {
  if (context) return scaleAwareChordQuestion(difficulty, octaveRange, style, speed, random, mode, context)
  const chordPool = absoluteChordPool(difficulty, mode)
  const chord = pick(chordPool, random)
  const availableOctaves = Math.min(3, Math.max(1, octaveRange))
  const root = 48 + Math.floor(random() * availableOctaves) * 12 + Math.floor(random() * 12)
  const maxInversion = maximumAbsoluteInversion(chord, difficulty, mode)
  const inversion = Math.floor(random() * (maxInversion + 1))
  const notes = voicedChord(chord, root, inversion)
  const rootName = midiName(root)
  const answerParts: QuestionAnswerParts = { root: rootName }
  const equivalentRoots = chord.inversionAmbiguous
    ? [...new Set(chord.intervals.map((interval) => midiName(root + interval)))]
    : [rootName]

  if (mode !== 'root') answerParts.quality = chord.name
  if (mode === 'root-quality-inversion') answerParts.inversion = inversionNames[inversion]

  const answer = [rootName, mode !== 'root' ? chord.name : '', mode === 'root-quality-inversion' ? inversionNames[inversion] : '']
    .filter(Boolean)
    .join(' · ')
  const qualityChoices = choicesFor(chord.name, chordPool.map((item) => item.name), random)
  const inversionChoices = inversionNames.slice(0, difficulty === 'advanced' ? inversionNames.length : maxInversion + 1)
  const kind: ExerciseKind = mode === 'root' ? 'chord-roots' : mode === 'root-quality' ? 'chords' : 'absolute-inversions'

  return {
    id: crypto.randomUUID(),
    kind,
    skill: mode === 'root'
      ? 'Absolute pitch · chord root'
      : mode === 'root-quality'
        ? 'Absolute pitch + harmony · root and quality'
        : 'Absolute pitch + harmony · chord and inversion',
    prompt: mode === 'root'
      ? 'What is the chord’s root note?'
      : mode === 'root-quality'
        ? 'Identify the root and chord quality.'
        : 'Identify the root, quality, and inversion.',
    answer,
    answerParts,
    acceptedAnswerParts: chord.inversionAmbiguous ? { root: equivalentRoots } : undefined,
    choices: mode === 'root' ? choicesFor(rootName, [...NOTE_NAMES], random, 6) : [],
    choiceGroups: mode === 'root'
      ? undefined
      : {
          root: [...NOTE_NAMES],
          quality: qualityChoices,
          ...(mode === 'root-quality-inversion' ? { inversion: inversionChoices } : {}),
        },
    events: chordEvents(notes, style, speed),
    explanation: `${answer}. The sounded notes were ${notes.map(midiName).join('–')}.${chord.inversionAmbiguous ? ` Because this chord is symmetrical, ${equivalentRoots.join(', ')} are accepted as equivalent roots.` : ''}`,
    detail: mode === 'root'
      ? 'Find the chord’s tonal root, even when another chord tone is in the bass.'
      : mode === 'root-quality'
        ? `Root pitch and harmonic quality are each worth half credit.${chord.inversionAmbiguous ? ' Equivalent roots of this symmetrical chord are accepted.' : ''}`
        : 'Identify the complete chord, then use the bass note to determine its inversion.',
  }
}

function progressionQuestion(selectedIds: string[] | undefined, difficulty: Difficulty, speed: number, random: () => number): Question {
  const eligibleProgressions = eligible(PROGRESSION_LIBRARY, difficulty)
  const selected = eligibleProgressions.filter((progression) => selectedIds?.includes(progression.id))
  const available = selected.length ? selected : eligibleProgressions
  const progression = pick(available, random)
  const tonic = 48 + Math.floor(random() * 12)
  const spacing = Math.max(0.42, 0.7 / speed)
  const sameLengthCandidates = eligibleProgressions
    .filter((candidate) => candidate.degrees.length === progression.degrees.length)
    .map((candidate) => candidate.name)
  const events = progression.degrees.map((degree, index) => ({
    notes: triadFor(tonic + degree, progression.qualities[index]),
    offset: index * spacing,
    duration: Math.max(0.58, spacing * 0.9),
  }))
  return {
    id: crypto.randomUUID(),
    kind: 'progressions',
    skill: `Progressions · ${progression.style}`,
    prompt: 'Which progression did you hear?',
    answer: progression.name,
    choices: choicesFor(progression.name, [...new Set(sameLengthCandidates)], random),
    events,
    explanation: `${progression.name} in ${midiName(tonic)}. ${progression.description}`,
    detail: progression.style,
  }
}

export function createQuestion(options: {
  kind: ExerciseKind
  difficulty: Difficulty
  octaveRange: number
  playbackStyle: PlaybackStyle
  playbackSpeed: number
  selectedScaleIds?: string[]
  selectedProgressionIds?: string[]
  absolutePitchSource?: AbsolutePitchSource
  absolutePitchTonic?: number
  absolutePitchScaleId?: string
  random?: () => number
}): Question {
  const random = options.random ?? Math.random
  const pitchContext = options.absolutePitchSource === 'scale'
    ? createScaleContext(options.absolutePitchTonic ?? 0, options.absolutePitchScaleId ?? 'ionian')
    : undefined
  switch (options.kind) {
    case 'notes':
      return noteQuestion(options.octaveRange, random, pitchContext)
    case 'intervals':
      return intervalQuestion(options.difficulty, options.playbackStyle, options.playbackSpeed, random)
    case 'scales':
      return scaleQuestion(options.selectedScaleIds, options.playbackSpeed, random)
    case 'chords':
      return absoluteChordQuestion(options.difficulty, options.octaveRange, options.playbackStyle, options.playbackSpeed, random, 'root-quality', pitchContext)
    case 'chord-roots':
      return absoluteChordQuestion(options.difficulty, options.octaveRange, options.playbackStyle, options.playbackSpeed, random, 'root', pitchContext)
    case 'absolute-inversions':
      return absoluteChordQuestion(options.difficulty, options.octaveRange, options.playbackStyle, options.playbackSpeed, random, 'root-quality-inversion', pitchContext)
    case 'inversions':
      return chordQuestion(options.difficulty, options.playbackStyle, options.playbackSpeed, random, true)
    case 'progressions':
      return progressionQuestion(options.selectedProgressionIds, options.difficulty, options.playbackSpeed, random)
  }
}

export const EXERCISE_COPY: Record<ExerciseKind, { title: string; eyebrow: string; description: string }> = {
  notes: {
    title: 'Note Identification',
    eyebrow: 'Absolute pitch',
    description: 'Identify individual notes by pitch class.',
  },
  'chord-roots': {
    title: 'Chord Root Identification',
    eyebrow: 'Absolute pitch',
    description: 'Identify the absolute root pitch of a chord in different voicings.',
  },
  intervals: {
    title: 'Interval Identification',
    eyebrow: 'Relative pitch',
    description: 'Identify the interval between two notes.',
  },
  scales: {
    title: 'Scale and Mode Identification',
    eyebrow: 'Scales and modes',
    description: 'Identify curated scales and modes from ascending examples.',
  },
  chords: {
    title: 'Root + Chord Quality',
    eyebrow: 'Absolute pitch + harmony',
    description: 'Identify both the chord root and its harmonic quality for proportional credit.',
  },
  'absolute-inversions': {
    title: 'Chord + Inversion',
    eyebrow: 'Absolute pitch + harmony',
    description: 'Identify the complete chord and its inversion from pitch alone.',
  },
  inversions: {
    title: 'Chord Inversions',
    eyebrow: 'Harmony',
    description: 'Identify root position and inversions by bass note.',
  },
  progressions: {
    title: 'Harmonic Progressions',
    eyebrow: 'Harmony',
    description: 'Identify common chord progressions by Roman numeral.',
  },
}
